import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  hrAttendanceMerchantApi,
  hrEmployeesApi,
  type AttendanceRecordDto,
  type AttendanceSummaryDto,
  type EmployeeDto,
} from '../../services/api';

function matchEmployee(emp: EmployeeDto, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  const fields = [
    emp.full_name,
    emp.first_name,
    emp.last_name,
    emp.email,
    emp.department,
    emp.job_title,
  ].filter(Boolean) as string[];
  return fields.some((f) => f.toLowerCase().includes(q));
}

function formatDateForInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/');
}

export default function AttendancePage() {
  const [selectedDate, setSelectedDate] = useState(formatDateForInput(new Date()));
  const [summary, setSummary] = useState<AttendanceSummaryDto>({
    total_employees: 0,
    present_today: 0,
    absent_today: 0,
    total_hours: 0,
  });
  const [records, setRecords] = useState<AttendanceRecordDto[]>([]);
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [quickEmployeeId, setQuickEmployeeId] = useState('');
  const [quickEmployeeSearch, setQuickEmployeeSearch] = useState('');
  const [quickDropdownOpen, setQuickDropdownOpen] = useState(false);
  const quickEmployeeRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    employee_id: '',
    date: formatDateForInput(new Date()),
    time_in: '09:00',
    time_out: '17:00',
    break_minutes: 0,
  });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrAttendanceMerchantApi.list(selectedDate);
      if (res.data) {
        setSummary(res.data.summary);
        setRecords(res.data.records);
      }
    } catch {
      setSummary({ total_employees: 0, present_today: 0, absent_today: 0, total_hours: 0 });
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await hrEmployeesApi.list();
      if (res.data) setEmployees(res.data);
    } catch {
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (quickEmployeeRef.current && !quickEmployeeRef.current.contains(e.target as Node)) {
        setQuickDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const quickFilteredEmployees = employees.filter((emp) => matchEmployee(emp, quickEmployeeSearch));
  const quickDisplayValue = quickEmployeeId
    ? (employees.find((e) => e.id === quickEmployeeId)?.full_name ?? quickEmployeeSearch)
    : quickEmployeeSearch;

  const handleQuickClockIn = async () => {
    if (!quickEmployeeId) return;
    setClockingIn(true);
    setAddError('');
    try {
      await hrAttendanceMerchantApi.clockInForEmployee(quickEmployeeId);
      await fetchAttendance();
      setQuickEmployeeId('');
      setQuickEmployeeSearch('');
    } catch (e: unknown) {
      const err = e as { message?: string };
      setAddError(err?.message || 'Failed to clock in.');
    } finally {
      setClockingIn(false);
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.employee_id) return;
    setAddSubmitting(true);
    setAddError('');
    try {
      await hrAttendanceMerchantApi.addManual({
        employee_id: addForm.employee_id,
        date: addForm.date,
        time_in: addForm.time_in,
        time_out: addForm.time_out,
        break_minutes: addForm.break_minutes || 0,
      });
      setAddModalOpen(false);
      setAddForm({
        employee_id: '',
        date: formatDateForInput(new Date()),
        time_in: '09:00',
        time_out: '17:00',
        break_minutes: 0,
      });
      await fetchAttendance();
    } catch (err: unknown) {
      const res = err as { message?: string; errors?: Record<string, string[]> };
      setAddError(res?.message || (res?.errors && Object.values(res.errors).flat().join(' ')) || 'Failed to add attendance.');
    } finally {
      setAddSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track employee time in and time out</p>
        </div>
        <button
          type="button"
          onClick={() => { setAddModalOpen(true); setAddError(''); }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8v8H4V4h8z" />
          </svg>
          + Add Attendance
        </button>
      </div>

      {/* Quick Clock In/Out */}
      <div className="bg-white rounded-xl shadow-card border border-gray-100 p-4 mb-4 shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-sm font-semibold text-gray-800">Quick Clock In/Out</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
          <div ref={quickEmployeeRef} className="relative flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              value={quickDisplayValue}
              onChange={(e) => {
                setQuickEmployeeSearch(e.target.value);
                setQuickEmployeeId('');
                setQuickDropdownOpen(true);
              }}
              onFocus={() => setQuickDropdownOpen(true)}
              placeholder="Select employee"
              className="w-full px-3 py-2.5 pr-9 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              aria-autocomplete="list"
              aria-expanded={quickDropdownOpen}
              aria-controls="quick-employee-list"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
            {quickDropdownOpen && (
              <ul
                id="quick-employee-list"
                role="listbox"
                className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-56 overflow-auto py-1"
              >
                {quickFilteredEmployees.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">No employees match</li>
                ) : (
                  quickFilteredEmployees.map((emp) => (
                    <li
                      key={emp.id}
                      role="option"
                      aria-selected={quickEmployeeId === emp.id}
                      onClick={() => {
                        setQuickEmployeeId(emp.id);
                        setQuickEmployeeSearch(emp.full_name);
                        setQuickDropdownOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setQuickEmployeeId(emp.id);
                          setQuickEmployeeSearch(emp.full_name);
                          setQuickDropdownOpen(false);
                        }
                      }}
                      className="px-3 py-2.5 text-sm text-gray-900 cursor-pointer hover:bg-brand-50 focus:bg-brand-50 outline-none"
                    >
                      {emp.full_name}
                      {emp.department && (
                        <span className="ml-2 text-gray-500 text-xs">— {emp.department}</span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={handleQuickClockIn}
            disabled={!quickEmployeeId || clockingIn}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            {clockingIn ? 'Clocking in...' : '→ Clock In'}
          </button>
          {addError && <p className="text-sm text-red-600">{addError}</p>}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 shrink-0">
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_employees}</p>
              <p className="text-xs font-medium text-gray-500">Total Employees</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.present_today}</p>
              <p className="text-xs font-medium text-gray-500">Present Today</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.absent_today}</p>
              <p className="text-xs font-medium text-gray-500">Absent Today</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_hours.toFixed(1)}</p>
              <p className="text-xs font-medium text-gray-500">Total Hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Date + Table */}
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <label className="text-sm font-medium text-gray-700">Date:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
        />
      </div>

      <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 text-sm">Loading...</div>
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <svg
              className="w-12 h-12 text-gray-300 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="font-medium text-gray-600">No attendance records for this date</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time In</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Out</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Break (min)</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Hours</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.employee_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDisplayDate(r.date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.time_in}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.time_out ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.break_minutes}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.total_hours != null ? r.total_hours.toFixed(1) : '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          r.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Attendance Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Attendance</h3>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select
                  required
                  value={addForm.employee_id}
                  onChange={(e) => setAddForm((f) => ({ ...f, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                >
                  <option value="">Select employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  required
                  value={addForm.date}
                  onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time In</label>
                  <input
                    type="time"
                    required
                    value={addForm.time_in}
                    onChange={(e) => setAddForm((f) => ({ ...f, time_in: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Out</label>
                  <input
                    type="time"
                    required
                    value={addForm.time_out}
                    onChange={(e) => setAddForm((f) => ({ ...f, time_out: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break (minutes)</label>
                <input
                  type="number"
                  min={0}
                  max={480}
                  value={addForm.break_minutes}
                  onChange={(e) => setAddForm((f) => ({ ...f, break_minutes: parseInt(e.target.value, 10) || 0 }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              {addError && <p className="text-sm text-red-600">{addError}</p>}
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => { setAddModalOpen(false); setAddError(''); }}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSubmitting}
                  className="px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {addSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
